import React, { Fragment, useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import { useSaleOpen } from "../../hooks/useSaleOpen";
import {
  exchangeCheckoutSession,
  verifyShopPayment,
  verifyAddonSubscription,
  verifyTierSubscription,
  buildReturnUrl,
  CheckoutSessionData,
  CheckoutSessionError,
} from "../../services/api/shopCheckoutService";
import {
  openRazorpayCheckout,
  openRazorpaySubscriptionCheckout,
} from "../../services/api/billingService";

import pricingBg from "../../images/bg/pricing-bg.png";

/**
 * /shop-checkout — the payment page for a tenant who already exists.
 *
 * Razorpay declines every AXOIX-owned charge opened on `<tenant>.axoix.io`,
 * because those subdomains are not registered on the AXOIX merchant account
 * (BR-115). axoix.com is registered, so the tenant's dashboard sends them here
 * with a single-use token and we open the sheet on this origin instead.
 *
 * This is NOT /checkout. That page signs a new tenant up: it reads
 * `location.state` from an in-app navigation, falls back to sessionStorage, and
 * failing both sends the visitor to /signup (CheckoutPage.tsx:227). Someone
 * arriving from another domain has neither, so they would be bounced to a
 * signup form they do not need — they already have an account and are only
 * buying a usage pack or an upgrade.
 *
 * What this page must never do: create a tenant, poll provisioning, or read a
 * subdomain from the URL. The subdomain comes out of the exchanged token, so a
 * hand-edited link cannot open a checkout against somebody else's account.
 */

type Phase = "loading" | "confirm" | "paying" | "verifying" | "done" | "expired" | "error";

const ShopCheckoutPage: React.FC = () => {
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>("loading");
  const [session, setSession] = useState<CheckoutSessionData | null>(null);
  const [message, setMessage] = useState<string>("");

  // testMode drives the "this charge isn't real" banner. Nobody should reach a
  // Pay button on a test key without being told — same rule as /checkout.
  const { testMode: paymentTestMode, loading: saleLoading } = useSaleOpen();

  // The session token is single-use and burned on exchange. React 18 StrictMode
  // mounts effects twice in development, which would spend the token on the
  // first run and show "already used" from the second. Guard the exchange.
  const exchangedRef = useRef(false);

  const sessionToken = new URLSearchParams(location.search).get("session");

  // ---- Step 1: exchange the token for the payment parameters -------------
  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    if (!sessionToken || !sessionToken.trim()) {
      // No redirect to /signup — this person is not signing up.
      setPhase("expired");
      setMessage(
        "This checkout link is missing its session. Please start again from your dashboard."
      );
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await exchangeCheckoutSession(sessionToken.trim());
        if (cancelled) return;
        setSession(data);
        setPhase("confirm");
      } catch (err) {
        if (cancelled) return;
        const e = err as CheckoutSessionError;
        setPhase(e.expired ? "expired" : "error");
        setMessage(e.message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionToken]);

  // ---- Step 3: verify, routed by kind ------------------------------------
  // Each kind verifies somewhere different, and all three prove the tenant with
  // `verify_token` rather than a tenant_id — the browser must not be the thing
  // naming a tenant (BR-115).
  const runVerify = useCallback(
    async (data: CheckoutSessionData, rzp: Record<string, string>) => {
      if (data.kind === "cart") {
        return verifyShopPayment({
          razorpay_order_id: rzp.razorpay_order_id,
          razorpay_payment_id: rzp.razorpay_payment_id,
          razorpay_signature: rzp.razorpay_signature,
          verify_token: data.verify_token,
        });
      }
      if (data.kind === "subscription") {
        return verifyAddonSubscription({
          razorpay_subscription_id: rzp.razorpay_subscription_id,
          razorpay_payment_id: rzp.razorpay_payment_id,
          razorpay_signature: rzp.razorpay_signature,
          verify_token: data.verify_token,
        });
      }
      return verifyTierSubscription({
        razorpay_subscription_id: rzp.razorpay_subscription_id,
        razorpay_payment_id: rzp.razorpay_payment_id,
        razorpay_signature: rzp.razorpay_signature,
        verify_token: data.verify_token,
      });
    },
    []
  );

  /**
   * Send the tenant home.
   *
   * ONLY ever `session.return_url`. It was validated at issue time to https on
   * *.axoix.io (shop.py:_validate_return_url); a return_url taken from our own
   * query string would turn this page into an open redirect.
   */
  const goBack = useCallback(
    (data: CheckoutSessionData, status: "success" | "failed" | "cancelled") => {
      window.location.href = buildReturnUrl(data.return_url, status);
    },
    []
  );

  // ---- Step 2: open Razorpay ---------------------------------------------
  const handlePay = useCallback(async () => {
    if (!session) return;
    setPhase("paying");
    setMessage("");

    const userInfo = { name: session.company_name || session.subdomain, email: "" };

    const onSuccess = async (response: Record<string, string>) => {
      setPhase("verifying");
      try {
        const result = await runVerify(session, response);
        if (result.success) {
          setPhase("done");
          goBack(session, "success");
        } else {
          // Signature checked out but activation reported a problem. The
          // dashboard is the only place that can show the real state, and the
          // webhook may still settle it, so hand back rather than guess.
          setPhase("error");
          setMessage(result.message || "We could not confirm this payment.");
          goBack(session, "failed");
        }
      } catch (err) {
        // The charge may well have gone through — never tell them it failed.
        // Both verify paths are deduped by razorpay_payment_id and the
        // subscription.charged / payment webhook still lands, so the dashboard
        // will catch up.
        setPhase("error");
        setMessage(
          "Your payment went through, but we could not confirm it here. It will appear in your dashboard shortly — please do not pay again."
        );
        goBack(session, "failed");
      }
    };

    const callbacks = {
      onSuccess: onSuccess as (r: any) => void,
      onDismiss: () => {
        // They closed the sheet. Nothing was charged.
        goBack(session, "cancelled");
      },
      onError: (err: unknown) => {
        setPhase("error");
        setMessage(err instanceof Error ? err.message : "Payment failed.");
        goBack(session, "failed");
      },
    };

    // The mothership already created the order/subscription at issue time, so
    // nothing is created here — which of the two it made is what picks the mode.
    // `tier_name` / `billing_cycle` only feed the sheet's notes. A usage pack
    // has neither, so the sheet's visible line comes from descriptionOverride
    // — the mothership's own description — rather than from those fields.
    const description = session.description || "AXOIX purchase";

    if (session.kind === "cart") {
      await openRazorpayCheckout(
        {
          order_id: session.order_id || "",
          amount: session.amount,
          currency: session.currency,
          tier_name: description,
          billing_cycle: session.items[0]?.billing_cycle || "one_time",
          razorpay_key_id: session.key_id,
        },
        userInfo,
        callbacks,
        description
      );
    } else {
      await openRazorpaySubscriptionCheckout(
        {
          subscription_id: session.subscription_id || "",
          amount: session.amount,
          currency: session.currency,
          tier_name: description,
          razorpay_key_id: session.key_id,
        },
        userInfo,
        callbacks,
        description
      );
    }
  }, [session, runVerify, goBack]);

  const inr = (paisa: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: session?.currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(paisa / 100);

  const shell = (title: string, body: React.ReactNode) => (
    <Fragment>
      <SEO
        title="Checkout"
        description="Complete your AXOIX purchase."
        path="/shop-checkout"
        noindex
      />
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <PageTitle pageTitle={title} pagesub="Checkout" />
            <section
              className="contact pt-100 pb-120 bg_img"
              style={{ backgroundImage: `url(${pricingBg})` }}
            >
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-7">{body}</div>
                </div>
              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );

  const card = (children: React.ReactNode) => (
    <div
      style={{
        background: "rgba(10, 25, 47, 0.75)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        borderRadius: "14px",
        padding: "32px",
      }}
    >
      {children}
    </div>
  );

  const notice = (tone: "warn" | "info", icon: string, heading: string, text: React.ReactNode) => {
    const colors =
      tone === "warn"
        ? { bg: "rgba(255, 170, 0, 0.12)", border: "rgba(255, 170, 0, 0.45)", fg: "#ffc861" }
        : { bg: "rgba(0, 255, 151, 0.10)", border: "rgba(0, 255, 151, 0.40)", fg: "#7dffc4" };
    return (
      <div
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "10px",
          padding: "15px 18px",
          marginBottom: "20px",
          color: colors.fg,
          fontSize: "13px",
          textAlign: "left",
        }}
      >
        <strong>
          <i className={icon} style={{ marginRight: "8px" }}></i>
          {heading}
        </strong>
        <div style={{ marginTop: "6px" }}>{text}</div>
      </div>
    );
  };

  const spinner = (text: string, sub?: string) => (
    <div className="text-center" style={{ padding: "40px 0" }}>
      <i
        className="fas fa-spinner fa-spin"
        style={{ fontSize: "40px", color: "var(--color-primary)", marginBottom: "20px" }}
      ></i>
      <p className="clr-white">{text}</p>
      {sub && (
        <p className="clr-white" style={{ fontSize: "14px", opacity: 0.7 }}>
          {sub}
        </p>
      )}
    </div>
  );

  // ---- Render -------------------------------------------------------------

  if (phase === "loading") {
    return shell("Opening checkout", card(spinner("Opening your checkout...")));
  }

  if (phase === "expired" || phase === "error") {
    return shell(
      phase === "expired" ? "Link expired" : "Checkout unavailable",
      card(
        <Fragment>
          {notice(
            "warn",
            "fas fa-link-slash",
            phase === "expired" ? "This checkout link is no longer valid" : "We could not open this checkout",
            message
          )}
          <p className="clr-white" style={{ fontSize: "14px", opacity: 0.8, margin: 0 }}>
            Nothing has been charged. Return to your AXOIX dashboard and start the
            purchase again from there.
          </p>
        </Fragment>
      )
    );
  }

  if (phase === "paying") {
    return shell(
      "Payment",
      card(spinner("Processing your payment...", "Please complete the payment in the Razorpay window."))
    );
  }

  if (phase === "verifying") {
    return shell(
      "Confirming",
      card(spinner("Confirming your payment...", "Please do not close this window."))
    );
  }

  if (phase === "done") {
    return shell("Payment complete", card(spinner("Taking you back to your dashboard...")));
  }

  // phase === "confirm"
  if (!session) return shell("Checkout", card(spinner("Opening your checkout...")));

  return shell(
    "Confirm your purchase",
    card(
      <Fragment>
        {paymentTestMode &&
          notice(
            "warn",
            "fas fa-flask",
            "TEST MODE",
            <Fragment>
              This checkout runs on a Razorpay <strong>test</strong> key. No money is
              charged and no invoice is raised. Use Razorpay's test card{" "}
              <code>4111 1111 1111 1111</code>, any future expiry, any CVV.
            </Fragment>
          )}

        {notice(
          "info",
          "fas fa-shield-halved",
          "You are on axoix.com",
          <Fragment>
            Paying for{" "}
            <strong>{session.company_name || session.subdomain}</strong> (
            {session.subdomain}.axoix.io). You will be returned to your dashboard
            when this is done.
          </Fragment>
        )}

        <h4 className="clr-white" style={{ marginBottom: "16px" }}>
          {session.description || "Your purchase"}
        </h4>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
          {session.items.map((item, i) => (
            <div
              key={`${item.type}-${item.id}-${i}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
                padding: "12px 0",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <span className="clr-white" style={{ fontSize: "14px" }}>
                {item.name || item.id}
                {item.quantity && item.quantity > 1 ? ` × ${item.quantity}` : ""}
                {item.billing_cycle && item.billing_cycle !== "one_time" && (
                  <span style={{ opacity: 0.6 }}> · billed {item.billing_cycle}</span>
                )}
              </span>
              {typeof item.price === "number" && (
                <span className="clr-white" style={{ fontSize: "14px", opacity: 0.85, whiteSpace: "nowrap" }}>
                  {new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: session.currency || "INR",
                    maximumFractionDigits: 2,
                  }).format(item.price)}
                </span>
              )}
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            padding: "18px 0 24px",
          }}
        >
          <strong className="clr-white">Total payable</strong>
          <strong className="clr-white" style={{ fontSize: "22px" }}>
            {inr(session.amount)}
          </strong>
        </div>
        <p className="clr-white" style={{ fontSize: "12px", opacity: 0.6, marginTop: "-18px", marginBottom: "22px" }}>
          Inclusive of GST.
        </p>

        <button
          className="cmn--btn"
          onClick={handlePay}
          disabled={saleLoading}
          style={{ width: "100%", opacity: saleLoading ? 0.6 : 1 }}
        >
          <span>{saleLoading ? "Please wait..." : `Pay ${inr(session.amount)}`}</span>
        </button>
      </Fragment>
    )
  );
};

export default ShopCheckoutPage;
